import { MIDDLE_OF_VISIBLE_BET_OPTIONS } from '@constants/betSlider'
import { groupBetsByGroup } from '@utils/betOption'
import { BetGroup, BetOptionItem } from 'game-ui-types'
import { create } from 'zustand'

export interface BetSliderStore {
    currentIndex: number
    activeIndexOffset: number
    activeIndex: number
    scrollToIndex: number
    updateActiveIndex: (index: number) => void
    updateCurrentIndex: (index: number) => void
    updateActiveIndexOffset: (index: number) => void
    updateScrollToIndex: (index: number) => void
    isScrolling: boolean
    setIsScrolling: (isScrolling: boolean) => void
    hideCentsWhenMultipleOf?: number
    setHideCentsWhenMultipleOf: (multiple?: number) => void
    forbiddenBetOptionIndexes: number[]
    setForbiddenBetOptionIndexes: (indexes: number[]) => void
    isEdgeReached: boolean
    setIsEdgeReached: (isEdgeReached: boolean) => void
    betGroup: BetGroup | null
    configureBetGroup: (bets: number[] | BetOptionItem[], betGroupId?: string, betOptionActiveIndex?: number) => void
    activeBetGroupId: string | null
    activeBetGroupBetOptions: BetOptionItem[] | null
    activeBetGroupBetIndex: number
    setActiveBetGroup: (activeBetGroupId: string, activeIndex?: number, activeBetGroupBets?: BetOptionItem[]) => void
    isScrollingFromBetChooser: boolean
    setIsScrollingFromBetChooser: (isScrolling: boolean) => void
    getActiveBetOption: () => BetOptionItem | null
}

export const useBetSliderStore = create<BetSliderStore>((set, get) => ({
    isScrollingFromBetChooser: false,
    setIsScrollingFromBetChooser: (isScrolling) => {
        set({ isScrollingFromBetChooser: isScrolling })
    },
    currentIndex: 0,
    activeIndexOffset: MIDDLE_OF_VISIBLE_BET_OPTIONS,
    activeIndex: MIDDLE_OF_VISIBLE_BET_OPTIONS,
    scrollToIndex: -1,
    updateScrollToIndex: (index) => {
        set({ scrollToIndex: index })
    },
    updateActiveIndexOffset: (index) => {
        set({ activeIndexOffset: index })
    },
    updateCurrentIndex: (index) => {
        set({ currentIndex: index })
    },
    updateActiveIndex: (index) => {
        set({ activeIndex: index })
    },
    isScrolling: false,
    setIsScrolling: (isScrolling) => {
        set({ isScrolling })
    },
    hideCentsWhenMultipleOf: undefined,
    setHideCentsWhenMultipleOf: (multiple) => {
        set({ hideCentsWhenMultipleOf: multiple })
    },
    forbiddenBetOptionIndexes: [],
    setForbiddenBetOptionIndexes: (indexes: number[]) => {
        set({ forbiddenBetOptionIndexes: indexes })
    },
    isEdgeReached: false,
    setIsEdgeReached: (isEdgeReached) => {
        set({ isEdgeReached })
    },
    betGroup: null,
    configureBetGroup: (bets, betGroupId, betOptionActiveIndex) => {
        const betGroup = groupBetsByGroup(bets)

        if (!betGroup) {
            set({ betGroup, activeBetGroupId: null, activeBetGroupBetOptions: null, activeBetGroupBetIndex: 0 })

            return
        }

        const activeIndex = betOptionActiveIndex ?? get().activeIndex
        const activeBetOptionItem = bets[activeIndex] as BetOptionItem
        const activeBetGroupId = betGroupId ?? activeBetOptionItem.betGroupId
        const activeBetGroupBetOptions = betGroup[activeBetGroupId]
        const activeBetGroupBetIndex = activeBetOptionItem.betGroupIndex

        set({
            betGroup,
            activeBetGroupId,
            activeBetGroupBetOptions,
            activeIndex,
            activeBetGroupBetIndex,
        })
    },
    activeBetGroupId: null,
    activeBetGroupBetOptions: null,
    setActiveBetGroup: (activeBetGroupId, activeIndex, betOptions) => {
        const betGroup = get().betGroup ?? {}
        const activeBetGroupBetIndex = Object.keys(betGroup).indexOf(activeBetGroupId)
        const activeBetGroupBetOptions = betOptions ?? betGroup[activeBetGroupId]

        set({
            activeBetGroupId,
            activeBetGroupBetOptions,
            activeIndex: activeIndex ?? get().activeIndex,
            activeBetGroupBetIndex,
        })
    },
    activeBetGroupBetIndex: 0,
    getActiveBetOption: () => {
        const activeBetGroupId = get().activeBetGroupId

        if (activeBetGroupId) {
            const betGroup = get().betGroup
            const activeIndex = get().activeIndex

            return betGroup?.[activeBetGroupId]?.[activeIndex] ?? null
        }

        return null
    },
}))
